import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { AnyObject, NumericObject } from '@/models/GenericInterfaces'
import { Country, Culture, Language, SimpleValueType } from '@/models/Global'
import { ProductInput } from '@/models/Import'
import { ProductPropertyStatus, SimplifiedProduct } from '@/models/Product'
import {
  AdvancedProperty,
  BasicBaseProperty,
  ComputedBaseProperty,
  EditableAdvancedProperty,
  EmptyCheckedPlaceholder,
  EmptyPreliminaryPlaceholder,
  FormattingFunction,
  PartialProperty,
  PropertyFilter,
  PropertyOptions,
  PropertySetterValueType,
  PropertyType,
  PropertyUsingComponent,
  PropertyValueType,
  StoredProperty,
  ValueCallback,
} from '@/models/Property'
import { RegisteredTaxonomy, SimpleTaxonomy, SimplifiedAttribute, SimplifiedCategory } from '@/models/Taxonomy'
import { useDefaultStore } from '@/store'
import { usePropertyStore } from '@/store/propertyStore'
import { useTaxonomiesStore } from '@/store/taxonomiesStore'
import { getFieldFilterLink, getTaxonomicFilterLink } from '@/utils/filterUtils'
import { compareAttribute, getImageUrl, getProductAttribute, getFallbackStringFromAttribute, sanitizeProduct } from '@/utils/productUtils'
import {
  getPropertyKey,
  isBarcodeProperty,
  isSkuProperty,
  productPropertyIsDeletable,
  productPropertyIsEditable,
  productPropertyStatusIsEditable,
  valueTypeIsNumeric,
} from '@/utils/propertyUtils'
import {
  areComparable,
  clone,
  deleteInnerValue,
  findPredicateResult,
  getCurrentValue,
  getInnerValue,
  getLocaleString,
  hasValue,
  numberToFixedDecimals,
  objectsAreEqual,
  setEnsuredInnerValue,
  toComparable,
  toNumber,
  toSafeString,
} from '@/utils/tools'
import { ComputeInstructions, computeInstructions } from '@burde/pim-shared'
import { capitalize } from 'vue'
import { RouteLocationAsPathGeneric, RouteLocationAsRelativeGeneric } from 'vue-router'
import DataService from './DataService'

type FieldPathArgs =
  | {
      type: 'attribute'
      args: { id: string }
    }
  | {
      type: 'languageSpecific' | 'countrySpecific' | 'basic'
      args: { culturePrefix: Country | Language | undefined; customPath: string[] }
    }
  | {
      type: 'simpleTaxonomy'
      args: { taxonomy: RegisteredTaxonomy }
    }
  | {
      type: 'categories' | 'category'
      args: { taxonomy: 'categories' }
    }
  | {
      type: 'computed'
      args: { defaultName: string }
    }

export default class PropertyService {
  private static get store() {
    return useDefaultStore()
  }

  private static get propertyStore() {
    return usePropertyStore()
  }

  private static get translate() {
    const { translate } = useTranslation()
    return translate
  }

  private static resetPropertyStatus = (productInput: ProductInput | undefined, path: string) => {
    const value = productInput?.propertyStatuses?.[path]
    delete productInput?.propertyStatuses?.[path]
    sanitizeProduct(productInput)
    return value !== undefined
  }

  private static setOrResetPropertyStatus = (
    productInput: ProductInput | undefined,
    path: string,
    status: ProductPropertyStatus | null,
    product: SimplifiedProduct | undefined,
    property: (StoredProperty<'attribute'> & { id: string }) | StoredProperty<Exclude<PropertyType, 'attribute'>>,
  ) => {
    if (productInput && productPropertyStatusIsEditable(status, property, product, productInput)) {
      const propertyKey = getPropertyKey(property)

      if (!product || product?.propertyStatuses?.[path] !== (status || undefined)) {
        if (!productInput.propertyStatuses) {
          productInput.propertyStatuses = {}
        }
        productInput.propertyStatuses[path] = status
        if (propertyKey) {
          this.invalidateDerivedProperties(product?.sku, propertyKey)
        }
        return true
      } else {
        this.resetPropertyStatus(productInput, path)
        if (propertyKey) {
          this.invalidateDerivedProperties(product?.sku, propertyKey)
        }
      }
    }
    return false
  }

  private static setInnerValue = (
    obj: AnyObject,
    outerKeyPath: Array<string | ValueCallback>,
    setObj: Partial<Record<string, unknown>>,
    level = 0,
  ): boolean => {
    try {
      const key = outerKeyPath[level]
      const hierarchicalObject = typeof key === 'function' ? key(obj) : obj[key]

      if (level < outerKeyPath.length - 1 && hierarchicalObject) {
        return this.setInnerValue(hierarchicalObject, outerKeyPath, setObj, level + 1)
      } else if (hierarchicalObject !== undefined) {
        Object.assign(hierarchicalObject, setObj)
        return true
      }
    } catch (err) {
      console.log(err, obj)
    }
    return false
  }

  private static getPlaceholder = (status: ProductPropertyStatus | null | undefined, fallback: string | number | undefined) =>
    fallback || (status === 'checked' ? EmptyCheckedPlaceholder : status === 'preliminary' ? EmptyPreliminaryPlaceholder : undefined)

  /**
   * Joins options
   * @returns formatted value
   * @returns displayValue = formatted value + potential suffix
   */
  private static getFormattedValue(
    storedProp: StoredProperty,
    translatedOptions: Array<string | number> | undefined,
    attribute?: SimplifiedAttribute,
    explicitTranslationLanguage?: Language,
    culture?: Partial<Culture>,
  ) {
    if (!translatedOptions?.length) {
      return
    }

    let value: string | number = '',
      suffix: string | number | undefined

    for (let i = 0; i < translatedOptions.length; i++) {
      if (i > 0) {
        value += ', '
      }

      const option = translatedOptions[i]

      const productAvailability = explicitTranslationLanguage
        ? this.store.settings?.properties?.productAvailabilityOptions.reduce<Partial<Record<number, string>>>((acc, option) => {
            acc[option.code] = this.translate(option.label, 'terms', 'capitalize', explicitTranslationLanguage)
            return acc
          }, {})
        : this.store.productAvailabilityCodeToString

      const formattingMethod = this.getPropertyFormattingInstructions(storedProp, attribute)
      const formattedOption = formattingMethod ? Formatting.get(formattingMethod, option, { productAvailability }) : option
      if (formattedOption) {
        value += formattedOption
      }

      suffix = Suffix.get(storedProp, culture)
    }

    return {
      value,
      displayValue: value && suffix ? `${value} ${suffix}` : value,
    }
  }

  private static invalidateDerivedProperties(sku: string | undefined, propertyKey: string) {
    const derived = this.propertyStore.derivedProperties.get(propertyKey)

    derived?.forEach((propertyKey) => {
      this.propertyStore.incPropertyResetKey(sku, propertyKey)
    })
  }

  private static getPropertyFormattingInstructions = (storedProp: StoredProperty, attribute?: SimplifiedAttribute) => {
    if (!storedProp.formatting) {
      return attribute?.valueType === 'float' || storedProp.valueType === 'float' ? 'toCommaNumeric' : undefined
    }

    return storedProp.formatting
  }

  private static getFieldPath({ type, args }: FieldPathArgs): string[] {
    switch (type) {
      case 'attribute':
        return ['attributes', args.id]
      case 'simpleTaxonomy':
        return [args.taxonomy]
      case 'basic':
      case 'languageSpecific':
      case 'countrySpecific':
        return args.culturePrefix ? [type, args.culturePrefix, ...args.customPath] : args.customPath
      case 'categories':
      case 'category':
        return [args.taxonomy]
      case 'computed':
        return [type, toComparable(args.defaultName)]
    }
  }

  private static getStatusPath(propertyType: PropertyType, fieldPath: string[]) {
    switch (propertyType) {
      case 'categories':
      case 'category':
        return 'categories'
      default:
        return fieldPath.join('/')
    }
  }

  public static getAttributeProperty(storedProp: StoredProperty<'attribute'>, attribute: SimplifiedAttribute): EditableAdvancedProperty {
    const propertyKey = getPropertyKey(attribute)

    const resetValue = (productInput: ProductInput | undefined, product?: SimplifiedProduct) => {
      if (productInput) {
        const lengthBeforeFilter = productInput.attributes?.length
        productInput.attributes = productInput.attributes?.filter((attributeInput) => !compareAttribute(attributeInput, attribute.defaultName, attribute.id))
        if (!productInput.attributes?.length) {
          delete productInput.attributes
        }
        this.invalidateDerivedProperties(product?.sku, propertyKey)
        return lengthBeforeFilter !== productInput?.attributes?.length
      }

      return false
    }

    const translationNamespace = 'terms'

    const fieldPath = this.getFieldPath({ type: storedProp.type, args: { id: attribute.id } })
    const statusPath = this.getStatusPath(storedProp.type, fieldPath)

    return {
      ...storedProp,
      ...attribute, // give prio to attribute settings over stored property when there are duplicates (e.g. defaultName) since that can be edited more easily through the taxonomy tool
      defaultHeader: this.getHeader(attribute.defaultName),
      headerTranslationNamespace: 'attributes',
      translationNamespace,
      sortFieldPath: fieldPath.join('.'),
      key: propertyKey,

      get: (product: SimplifiedProduct | undefined, productInput?: ProductInput, explicitTranslationLanguage?: Language) => {
        const productAttribute = getProductAttribute(product, attribute.defaultName, attribute.id)
        const productAttributeInput = getProductAttribute(productInput, attribute.defaultName, attribute.id)
        const currentProductAttribute = productAttributeInput ?? productAttribute

        let filters: PropertyFilter[] | undefined

        const defaultOptions = currentProductAttribute?.options?.map((option) => (typeof option === 'object' ? option.defaultName : option) as string)
        const translatedOptions = currentProductAttribute?.options?.map((option) => {
          if (typeof option === 'object') {
            return option.name
          } else if (attribute.hasTerms) {
            return this.translate(
              // translate ProductInput values
              option,
              translationNamespace,
              'capitalize',
              explicitTranslationLanguage,
              undefined,
              attribute.translateAlphabeticals,
            )
          } else {
            return option
          }
        })

        if (product) {
          filters = productInput
            ? undefined
            : productAttribute?.options?.reduce((acc: PropertyFilter[], option) => {
                acc.push({
                  displayValue: option.name,
                  link: getTaxonomicFilterLink('attributes', productAttribute, option),
                })

                return acc
              }, [])
        }

        const formatted = this.getFormattedValue(storedProp, translatedOptions, attribute, explicitTranslationLanguage)

        const originalStatus = product?.propertyStatuses?.[statusPath]
        const statusInput = productInput?.propertyStatuses?.[statusPath]

        const fallback = getFallbackStringFromAttribute(productAttribute, 'name')

        const currentStatus = getCurrentValue(statusInput, originalStatus)
        const placeholder = this.getPlaceholder(currentStatus, fallback)

        return {
          defaultOptions,
          options: translatedOptions,

          displayValue: formatted?.displayValue,
          value: formatted?.value,

          placeholder,
          fallback,

          delete: currentProductAttribute?.options === null,
          filters,
          status: currentStatus,
          modified: {
            value: !!productAttributeInput,
            status: statusInput !== undefined,
          },
        }
      },
      set: (productInput: ProductInput | undefined, value: PropertySetterValueType, product: SimplifiedProduct | undefined) => {
        if (productPropertyIsEditable(storedProp)) {
          if (productInput && value !== undefined) {
            const compareValue = typeof value === 'number' ? String(value) : value
            const shouldDelete = compareValue === null || compareValue.length === 0
            const options = Array.isArray(value) ? value : shouldDelete ? null : [value!]

            const productAttribute = getProductAttribute(product, storedProp.defaultName, attribute?.id)

            if (
              !product ||
              (shouldDelete
                ? !!productAttribute?.options?.length
                : productAttribute?.options?.length !== options?.length ||
                  productAttribute?.options?.some((o, i) => !areComparable(options?.[i], o.defaultName)))
            ) {
              const productAttributeInput = getProductAttribute(productInput, attribute.defaultName, attribute.id)

              if (productAttributeInput) {
                productAttributeInput.delete = shouldDelete
                productAttributeInput.options = options
              } else {
                // add value when not found
                productInput.attributes ??= []
                productInput.attributes.push({
                  id: attribute.id,
                  defaultName: storedProp.defaultName,
                  options,
                  delete: shouldDelete,
                })
              }

              this.invalidateDerivedProperties(product?.sku, propertyKey)
              return true
            }
          }
          resetValue(productInput, product)
        }
        return false
      },
      reset: resetValue,
      setStatus: (productInput: ProductInput | undefined, status: ProductPropertyStatus | null, product: SimplifiedProduct | undefined) =>
        this.setOrResetPropertyStatus(productInput, statusPath, status, product, { ...storedProp, id: attribute.id }),
      resetStatus: (productInput: ProductInput | undefined) => this.resetPropertyStatus(productInput, statusPath),
    }
  }

  public static getSimpleTaxonomyProperty(storedProp: StoredProperty<'simpleTaxonomy'>): EditableAdvancedProperty {
    const taxonomy = storedProp.defaultName.toLowerCase() as SimpleTaxonomy
    const propertyKey = getPropertyKey(storedProp)

    const resetValue = (productInput?: ProductInput, product?: SimplifiedProduct) => {
      if (productInput?.[taxonomy] !== undefined) {
        delete productInput?.[taxonomy]
        this.invalidateDerivedProperties(product?.sku, propertyKey)
        return true
      }
      return false
    }

    const fieldPath = this.getFieldPath({ type: storedProp.type, args: { taxonomy } })
    const statusPath = this.getStatusPath(storedProp.type, fieldPath)

    return {
      ...storedProp,
      defaultHeader: this.getHeader(taxonomy),
      translationNamespace: taxonomy,
      headerTranslationNamespace: 'taxonomies',
      allowMultipleValues: true,
      valueType: 'string',
      sortFieldPath: statusPath,
      key: propertyKey,

      get: (product: SimplifiedProduct | undefined, productInput: ProductInput | undefined, explicitTranslationLanguage?: Language) => {
        const originalValue = product?.[taxonomy]
        const inputValue = productInput?.[taxonomy]
        const currentValue = getCurrentValue(inputValue, originalValue)

        const defaultOptions = currentValue?.map((option) => (typeof option === 'object' ? option.defaultName : option))
        const translatedOptions = currentValue?.map((option) => {
          if (typeof option === 'object') {
            return option.name
          } else {
            return this.translate(option, taxonomy, 'capitalize', explicitTranslationLanguage)
          }
        })

        const formatted = this.getFormattedValue(storedProp, translatedOptions, undefined, explicitTranslationLanguage)

        const filters = product?.[taxonomy]?.reduce((acc: PropertyFilter[], option) => {
          if (typeof option === 'object') {
            acc.push({
              displayValue: option.name,
              link: getTaxonomicFilterLink(taxonomy, option),
            })
          }
          return acc
        }, [])

        const statusInput = productInput?.propertyStatuses?.[statusPath]
        const originalStatus = product?.propertyStatuses?.[statusPath]

        const currentStatus = getCurrentValue(statusInput, originalStatus)

        return {
          defaultOptions,
          options: translatedOptions,

          value: formatted?.value,
          displayValue: formatted?.displayValue,

          filters,
          delete: currentValue === null,
          status: currentStatus,
          placeholder: this.getPlaceholder(currentStatus, undefined),
          modified: {
            value: inputValue !== undefined,
            status: statusInput !== undefined,
          },
        }
      },
      set: (productInput: ProductInput | undefined, value: PropertySetterValueType, product: SimplifiedProduct | undefined) => {
        if (productInput) {
          const formattedValue = Array.isArray(value) ? value : value ? [value] : null
          const shouldBeDeleted = formattedValue === null

          const original = product?.[taxonomy]

          if (product) {
            if (
              shouldBeDeleted
                ? original?.length
                : original?.length !== formattedValue?.length || original?.some((o, i) => !areComparable(o.defaultName, formattedValue?.[i]))
            ) {
              productInput[taxonomy] = formattedValue as string[] | null

              this.invalidateDerivedProperties(product?.sku, propertyKey)
              return true
            }

            resetValue(productInput, product)
          }
        }
        return false
      },
      reset: resetValue,
      setStatus: (productInput: ProductInput | undefined, status: ProductPropertyStatus | null, product: SimplifiedProduct | undefined) =>
        this.setOrResetPropertyStatus(productInput, statusPath, status, product, storedProp),
      resetStatus: (productInput: ProductInput | undefined) => this.resetPropertyStatus(productInput, statusPath),
    }
  }

  public static getBasicProperty(
    storedProp: StoredProperty<'basic' | 'languageSpecific' | 'countrySpecific'>,
    partialCulture?: Partial<Culture>,
  ): EditableAdvancedProperty {
    const isItemNo = isSkuProperty(storedProp)
    const propertyKey = getPropertyKey(storedProp)

    const cultureSpecificity = storedProp.type === 'languageSpecific' ? 'language' : storedProp.type === 'countrySpecific' ? 'country' : undefined
    const culturePrefix = cultureSpecificity && partialCulture?.[cultureSpecificity]

    const fieldPath = this.getFieldPath({
      type: storedProp.type,
      args: { customPath: storedProp.customPath, culturePrefix },
    })

    const statusPath = this.getStatusPath(storedProp.type, fieldPath)
    const sortFieldPath = fieldPath.join('.')

    const resetValue = (productInput?: ProductInput, product?: SimplifiedProduct) => {
      const result = deleteInnerValue(productInput, fieldPath)
      sanitizeProduct(productInput)
      this.invalidateDerivedProperties(product?.sku, propertyKey)
      return result
    }

    return {
      ...storedProp,
      defaultHeader: this.getHeader(storedProp.defaultName, culturePrefix),
      headerTranslationNamespace: 'attributes',
      culturePrefix,
      ...(valueTypeIsNumeric(storedProp.valueType) && {
        step: storedProp.valueType === 'int' ? 1 : storedProp.formatting === 'toPriceFormat' ? 0.01 : 0.1,
      }),
      valueType: storedProp.valueType || 'string',
      sortFieldPath,
      key: propertyKey,

      /**
       * @param culture should be used for cultural props without culturePrefix. also controls translations
       */
      get: (product: SimplifiedProduct | undefined, productInput: ProductInput | undefined, explicitTranslationLanguage?: Language) => {
        const originalValue = getInnerValue<SimpleValueType | NumericObject>(product, fieldPath)
        const inputValue = getInnerValue<SimpleValueType | NumericObject>(productInput, fieldPath)
        const currentValue = getCurrentValue(inputValue, originalValue) as any

        let defaultOptions: PropertyOptions | undefined
        let translatedOptions: PropertyOptions | undefined
        let displayValue: string | undefined
        let value: string | number | undefined
        let filters:
          | {
              displayValue: string
              link: string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric
            }[]
          | undefined

        if (hasValue(currentValue)) {
          if (!isItemNo || !product?.hasTemporarySku || productInput?.sku) {
            defaultOptions = Array.isArray(currentValue) ? currentValue : hasValue(currentValue) ? [currentValue] : undefined

            translatedOptions = storedProp.translationNamespace
              ? defaultOptions?.map((val) => this.translate(val, storedProp.translationNamespace!, 'capitalize', explicitTranslationLanguage))
              : defaultOptions

            const formatted = this.getFormattedValue(
              storedProp,
              translatedOptions,
              undefined,

              explicitTranslationLanguage,
              partialCulture,
            )
            value = formatted?.value
            displayValue = formatted?.displayValue
          }

          if (isItemNo && product?.hasTemporarySku && !productInput?.sku) {
            displayValue = ''
          }

          if (product && displayValue !== undefined && !isBarcodeProperty(storedProp)) {
            filters = [
              {
                displayValue,
                link: isItemNo
                  ? { name: 'product', params: { sku: currentValue }, state: { modal: true } }
                  : getFieldFilterLink(storedProp, fieldPath, currentValue),
              },
            ]
          }
        }

        const statusPath = this.getStatusPath(storedProp.type, fieldPath)

        // const statusPath =
        //   cultureSpecificity && !culturePrefix
        //     ? `${storedProp.type}/${this.store.dataCulture?.[cultureSpecificity]}/${storedProp.customPath?.join('/') ?? ''}`
        //     : paths.join('/')

        const originalStatus = product?.propertyStatuses?.[statusPath]
        const statusInput = productInput?.propertyStatuses?.[statusPath]
        const currentStatus = getCurrentValue(statusInput, originalStatus)

        const isDeleted = currentValue === '' || currentValue === null // TODO ought to be null always for consistency

        return {
          defaultOptions,
          options: translatedOptions,

          value,
          displayValue,

          delete: isDeleted,
          filters,
          status: currentStatus,
          placeholder: this.getPlaceholder(currentStatus, undefined),
          modified: {
            value: inputValue !== undefined,
            status: statusInput !== undefined,
          },
        }
      },
      set: (productInput: ProductInput | undefined, value: PropertySetterValueType, product: SimplifiedProduct | undefined) => {
        if (!productInput || !productPropertyIsEditable({ defaultName: storedProp.defaultName, readonly: storedProp.readonly }, product, productInput)) {
          return false
        }

        if ((value !== '' && value !== null) || productPropertyIsDeletable(storedProp, product, productInput)) {
          let formattedValue

          if (value == null) {
            formattedValue = null
          } else if (storedProp.allowMultipleValues) {
            formattedValue = Array.isArray(value) ? value : [value]
          } else {
            formattedValue = Array.isArray(value) ? value[0] : value
          }

          if (!storedProp.customPath) {
            console.log('No customPath for basic prop', storedProp.defaultName)
            return false
          }

          const original = getInnerValue<PropertySetterValueType>(product, fieldPath)

          if (!product || !objectsAreEqual(original, value, undefined, false)) {
            setEnsuredInnerValue(productInput, fieldPath, formattedValue)
            this.invalidateDerivedProperties(product?.sku, propertyKey)
            return true
          } else {
            resetValue(productInput, product)
          }
        }

        return false
      },
      reset: resetValue,
      setStatus: (productInput: ProductInput | undefined, status: ProductPropertyStatus | null, product: SimplifiedProduct | undefined) =>
        this.setOrResetPropertyStatus(productInput, statusPath, status, product, storedProp),
      resetStatus: (productInput: ProductInput | undefined) => this.resetPropertyStatus(productInput, statusPath),
    }
  }

  public static getCategoriesProperty(storedProp: StoredProperty<'categories'>): EditableAdvancedProperty {
    const statusPath = this.getStatusPath(storedProp.type, [storedProp.type])

    const propertyKey = getPropertyKey(storedProp)

    const resetValue = (productInput?: ProductInput, product?: SimplifiedProduct) => {
      if (productInput?.categories !== undefined) {
        delete productInput?.categories
        this.invalidateDerivedProperties(product?.sku, propertyKey)
        return true
      }
      return false
    }

    return {
      ...storedProp,
      defaultHeader: this.getHeader(storedProp.type),
      headerTranslationNamespace: 'taxonomies',
      translationNamespace: storedProp.type,
      allowMultipleValues: true,
      sortFieldPath: statusPath,
      valueType: 'string',
      key: propertyKey,

      get: (product: SimplifiedProduct | undefined, productInput: ProductInput | undefined, explicitTranslationLanguage?: Language) => {
        const originalStatus = product?.propertyStatuses?.[statusPath]
        const statusInput = productInput?.propertyStatuses?.[statusPath]
        const currentStatus = getCurrentValue(statusInput, originalStatus)
        const placeholder = this.getPlaceholder(currentStatus, undefined)

        const currentOptions = productInput?.categories ?? product?.categories.map((c) => c.name)
        const translatedOptions = currentOptions?.map((name) =>
          this.translate(name, storedProp.type, 'capitalize', explicitTranslationLanguage, undefined, true),
        )

        const formattedValue = this.getFormattedValue(storedProp, translatedOptions, undefined, explicitTranslationLanguage)

        const filters = product?.categories?.reduce((acc: PropertyFilter[], option) => {
          if (option) {
            acc.push({
              displayValue: option.name,
              link: getTaxonomicFilterLink(storedProp.type, option),
            })
          }

          return acc
        }, [])

        return {
          defaultOptions: currentOptions,
          options: translatedOptions,

          value: formattedValue?.value,
          displayValue: formattedValue?.displayValue,

          delete: currentOptions === null,
          filters,
          status: currentStatus,
          placeholder,
          modified: {
            value: productInput?.categories !== undefined,
            status: statusInput !== undefined,
          },
        }
      },
      set: (productInput: ProductInput | undefined, value: PropertySetterValueType, product: SimplifiedProduct | undefined) => {
        if (productInput) {
          const formattedValue = Array.isArray(value) ? value : value ? [value] : null
          const original = product?.categories

          if (!product || !objectsAreEqual(original, formattedValue)) {
            productInput.categories = formattedValue as string[] | null
            this.invalidateDerivedProperties(product?.sku, propertyKey)
          } else {
            resetValue(productInput)
          }

          return true
        }
        return false
      },
      reset: resetValue,
      setStatus: (productInput: ProductInput | undefined, status: ProductPropertyStatus | null, product: SimplifiedProduct | undefined) =>
        this.setOrResetPropertyStatus(productInput, statusPath, status, product, storedProp),
      resetStatus: (productInput: ProductInput | undefined) => this.resetPropertyStatus(productInput, statusPath),
    }
  }

  public static getCategoryProperty(storedProp: StoredProperty<'category'>): EditableAdvancedProperty {
    const index = Number(storedProp.defaultName.replace('Category', '')?.trim()) - 1

    const type = 'categories'

    return {
      ...storedProp,
      defaultHeader: storedProp.defaultName,
      headerTranslationNamespace: 'taxonomies',
      translationNamespace: type,
      sortFieldPath: type,
      valueType: 'string',
      key: getPropertyKey(storedProp),

      get: (product: SimplifiedProduct | undefined, productInput: ProductInput | undefined, explicitTranslationLanguage?: Language) => {
        const categoryDefaultName = productInput?.categories?.[index] || product?.categories?.[index]?.defaultName

        const defaultOptions = categoryDefaultName ? [categoryDefaultName] : undefined
        // translations will fail for words with multiple spaces when using translateAlphabeticals true
        const translatedValue = this.translate(categoryDefaultName, 'categories', 'capitalize', explicitTranslationLanguage, undefined, true)
        const translatedOptions = translatedValue ? [translatedValue] : undefined

        const formatted = this.getFormattedValue(storedProp, translatedOptions, undefined, explicitTranslationLanguage)

        const originalStatus = product?.propertyStatuses?.[type]
        const statusInput = productInput?.propertyStatuses?.[type]
        const currentStatus = getCurrentValue(statusInput, originalStatus)

        return {
          defaultOptions,
          options: translatedOptions,
          value: formatted?.value,
          displayValue: formatted?.displayValue,
          status: currentStatus,
        }
      },
      set: (productInput: ProductInput | undefined, value: PropertySetterValueType, product: SimplifiedProduct | undefined) => {
        console.log(`setter not implemented for ${storedProp.defaultName}`)
        return false
      },
      reset: (productInput: ProductInput | undefined) => {
        console.log(`reset not implemented for ${storedProp.defaultName}`)
        return false
      },
      setStatus: (productInput: ProductInput | undefined, status: ProductPropertyStatus | null, product: SimplifiedProduct | undefined) => {
        console.log(`setStatus not implemented for ${storedProp.defaultName}`)
        return false
      },
      resetStatus: (productInput: ProductInput | undefined) => {
        console.log(`resetStatus not implemented for ${storedProp.defaultName}`)
        return false
      },
    }
  }

  public static getComputedProperty(storedProp: ComputedBaseProperty, instructions: ComputeInstructions[]): EditableAdvancedProperty {
    const propertyKey = getPropertyKey(storedProp)
    const simplifiedProductPropertyKey = storedProp.defaultName.toLowerCase() // yeah.

    const dependantPropertiesPaths = storedProp.dependencies?.reduce<Array<{ fieldPath: string[]; statusPath: string }>>((acc, propertyKey) => {
      const property = this.propertyStore.getPropertyByKey(propertyKey)

      if (!property) {
        return acc
      }

      const fieldPathArgs =
        property.type === 'attribute'
          ? { type: property.type, args: { id: propertyKey } }
          : property.type === 'basic' || property.type === 'languageSpecific' || property.type === 'countrySpecific'
            ? { type: property.type, args: { customPath: property.customPath, culturePrefix: undefined } }
            : undefined

      if (!fieldPathArgs) {
        return acc
      }

      const fieldPath = this.getFieldPath(fieldPathArgs)
      const statusPath = this.getStatusPath(property.type, fieldPath)
      acc.push({ fieldPath, statusPath })

      return acc
    }, [])

    return {
      ...storedProp,
      defaultHeader: storedProp.defaultName,
      headerTranslationNamespace: 'attributes',
      sortFieldPath: `computed.${storedProp.defaultName.toLowerCase()}`,
      valueType: 'string',
      key: propertyKey,
      readonly: true,

      get: (product: SimplifiedProduct | undefined, productInput: ProductInput | undefined, explicitTranslationLanguage?: Language) => {
        const currentValue = productInput
          ? computeInstructions(instructions, null, storedProp, product, productInput, undefined, this.propertyStore.propertyMappings)
          : product?.computed?.[simplifiedProductPropertyKey]?.options

        const defaultOptions = currentValue as string[] | undefined
        const formatted = this.getFormattedValue(storedProp, defaultOptions, undefined, explicitTranslationLanguage)

        const status = findPredicateResult(dependantPropertiesPaths, (property) => {
          const originalStatus = product?.propertyStatuses?.[property.statusPath]
          const inputStatus = productInput?.propertyStatuses?.[property.statusPath]
          const currentStatus = getCurrentValue(inputStatus, originalStatus)

          if (currentStatus === 'preliminary') {
            return currentStatus
          }
        })

        return {
          defaultOptions,
          options: defaultOptions,
          displayValue: formatted?.displayValue,
          value: formatted?.value,
          status,
        }
      },
      set: (productInput: ProductInput | undefined, value: PropertySetterValueType, product: SimplifiedProduct | undefined, culture?: Culture) => {
        // console.log(`setter not implemented for ${storedProp.defaultName}`)
        return false
      },
      reset: (productInput: ProductInput | undefined) => {
        // console.log(`reset not implemented for ${storedProp.defaultName}`)
        return false
      },
      setStatus: (productInput: ProductInput | undefined, status: ProductPropertyStatus | null, product: SimplifiedProduct | undefined) => {
        // console.log(`setStatus not implemented for ${storedProp.defaultName}`)
        return false
      },
      resetStatus: (productInput: ProductInput | undefined) => {
        // console.log(`resetStatus not implemented for ${storedProp.defaultName}`)
        return false
      },
    }
  }

  public static async getPropertyGroups(
    usingComponent: PropertyUsingComponent,
    dataCulture?: Culture | null,
    customTranslationLanguage?: Language,
  ): Promise<EditableAdvancedProperty[][]> {
    const allAttributesById = await useTaxonomiesStore().getTaxonomicDataById('attributes')

    const getCultureSpecificProperty = (storedProp: BasicBaseProperty, partialCultulre?: Partial<Culture>): EditableAdvancedProperty => {
      return this.getBasicProperty(storedProp, partialCultulre)
    }

    await Promise.all([DataService.loadProperties()])

    const properties: EditableAdvancedProperty[][] = this.propertyStore.storedProperties.map((propertyGroup) => {
      return propertyGroup.flatMap((storedProp) => {
        try {
          switch (storedProp.type) {
            case 'attribute': {
              const attribute = storedProp.id ? allAttributesById.get(storedProp.id) : undefined

              if (!attribute) {
                return []
              }

              return this.getAttributeProperty(storedProp, {
                ...{ ...attribute, terms: undefined, staticValues: undefined },
              })
            }
            case 'languageSpecific': {
              return [
                getCultureSpecificProperty(storedProp),
                ...this.store.availableLanguages.map((language: Language) => getCultureSpecificProperty(storedProp, { language })),
              ]
            }
            case 'countrySpecific': {
              return [
                getCultureSpecificProperty(storedProp),
                ...this.store.availableCountries.map((country: Country) => getCultureSpecificProperty(storedProp, { country })),
              ]
            }
            case 'categories': {
              return this.getCategoriesProperty(storedProp)
            }
            case 'category': {
              return this.getCategoryProperty(storedProp)
            }
            case 'simpleTaxonomy': {
              return this.getSimpleTaxonomyProperty(storedProp)
            }
            case 'basic': {
              return this.getBasicProperty(storedProp, undefined)
            }
            case 'computed': {
              const instructions = this.propertyStore.getPropertyComputeInstructions(storedProp)
              if (!instructions) {
                console.log(`Missing instructions for ${storedProp.defaultName}. Rejecting property`)
                return []
              }
              return this.getComputedProperty(storedProp, instructions)
            }

            default: {
              console.log(`found no property mapping for ${(storedProp as any)?.defaultName} (undefined property type)`)
              console.log(storedProp)
              console.log('-----')

              return []
            }
          }
        } catch (err) {
          console.warn(storedProp.defaultName, err)
          return []
        }
      })
    })

    const addedProperties = new Set<string>()
    properties.forEach((g) =>
      g.forEach((p) => {
        addedProperties.add(p.key)
      }),
    )

    const attributes = await useTaxonomiesStore().getTaxonomicData('attributes')

    if (attributes) {
      // making sure non mapped attributes are available by adding them last
      const unmappedAttributes = attributes
        .filter((attribute): attribute is SimplifiedAttribute => !!attribute && !addedProperties.has(getPropertyKey(attribute)) && !attribute.disabled)
        .sort((a, b) => String(a.defaultName).localeCompare(String(b.defaultName)))
        .flatMap(
          (attribute) =>
            this.getAttributeProperty(
              { defaultName: attribute.defaultName, type: 'attribute' },
              attribute,
              // getTypeFormatting(attribute.valueType)
            ) || [],
        )

      properties.push(unmappedAttributes)
    }

    // translate header
    properties.forEach((group) => {
      group.forEach((property) => {
        property.name = this.translate(
          property.defaultName,
          property.headerTranslationNamespace,
          'capitalize',
          customTranslationLanguage || this.store.translationLanguage,
        )

        property.header = this.getHeader(property.name, property.culturePrefix)
      })
    })

    return properties
      .filter((propertyGroup) => propertyGroup.length)
      .map((propertyGroup) =>
        propertyGroup.filter(
          (property) =>
            (!dataCulture?.country || !property.exclusiveToCountries?.length || property.exclusiveToCountries.includes(dataCulture.country)) &&
            (!property.exclusiveTo || property.exclusiveTo.includes(usingComponent)),
        ),
      )
  }

  static getHeader = (name: string | number, culturePrefix?: string) => (culturePrefix ? `${culturePrefix.toUpperCase()} ${name}` : name.toString())

  static getTranslatedHeader(partialProperty: PartialProperty, translationLanguage?: Language) {
    const name =
      translationLanguage && partialProperty.headerTranslationNamespace
        ? this.translate(partialProperty.defaultName, partialProperty.headerTranslationNamespace, 'capitalize', translationLanguage)
        : capitalize(partialProperty.defaultName)

    return this.getHeader(name, partialProperty.culturePrefix)
  }
}

class Suffix {
  private static get tools() {
    return useTools()
  }

  public static get(storedProp: StoredProperty, culture?: Partial<Culture>) {
    switch (storedProp.suffix) {
      case 'getCurrency':
        return culture?.country && this.tools.getCurrency(culture.country)
    }
  }
}

class Formatting {
  private static get store() {
    return useDefaultStore()
  }

  private static get localeString() {
    return (this.store.dataCulture && getLocaleString(this.store.dataCulture)) ?? undefined
  }

  private static toCommaNumeric = (carriedValue: unknown) => toSafeString(toNumber(carriedValue)).replace('.', ',')

  private static toStringFormat = toSafeString

  private static getImages = (value: unknown) => (Array.isArray(value) ? value.map((name) => getImageUrl(name)).join(', ') : getImageUrl(value as string))

  private static availabilityCodeToLabel = (value: unknown, mappings: any) => {
    const statusCode = toNumber(value)
    return mappings?.productAvailability?.[statusCode]
  }

  public static get(formattingMethod: FormattingFunction, value: unknown, mappings?: any) {
    switch (formattingMethod) {
      case 'toPriceFormat':
        return numberToFixedDecimals(value, 2, this.localeString)
      case 'toWeightFormat':
        return numberToFixedDecimals(value, 1, this.localeString)
      case 'toDimensionsFormat':
        return numberToFixedDecimals(value, 0, this.localeString)
      case 'toCommaNumeric':
        return this.toCommaNumeric(value)
      case 'toStringFormat':
        return this.toStringFormat(value)
      case 'getImages':
        return this.getImages(value)
      case 'toAvailabilityStatusLabel':
        return this.availabilityCodeToLabel(value, mappings)
      default:
        return value
    }
  }
}
